import { defineStore } from "pinia"
import { ref, computed } from "vue"
import NavigationService, { type NavigationItem } from "@/api/NavigationService"

/**
 * Navigation store for managing the shared navigation state.
 * Fetches navigation structure from the backend API which respects user permissions.
 */
export const useNavigationStore = defineStore("navigationStore", () => {
  const items = ref<NavigationItem[]>([])
  const isLoading = ref(false)
  const isLoaded = ref(false)
  const error = ref<string | null>(null)

  /**
   * Check if navigation has items
   */
  const hasItems = computed(() => items.value.length > 0)

  /**
   * Load navigation from the backend API.
   * Only loads once per session (cached in store).
   * Call with force=true to reload.
   */
  async function loadNavigation(force = false): Promise<void> {
    // Skip if already loaded and not forcing reload
    if (isLoaded.value && !force) {
      return
    }

    // Skip if already loading
    if (isLoading.value) {
      return
    }

    // Reset loaded state when forcing reload
    if (force) {
      isLoaded.value = false
    }

    isLoading.value = true
    error.value = null

    try {
      const service = new NavigationService()
      const response = await service.getNavigation()

      if (response.success && response.data) {
        items.value = response.data
        isLoaded.value = true
      } else {
        // Navigation failed but app continues to work
        error.value = response.error || "Fehler beim Laden der Navigation"
        isLoaded.value = true // Mark as loaded to prevent retry loops
        console.warn("Navigation load failed (non-critical):", response.error)
      }
    } catch (e) {
      // Navigation failed but app continues to work
      error.value = "Netzwerkfehler beim Laden der Navigation"
      isLoaded.value = true // Mark as loaded to prevent retry loops
      console.warn("Navigation load error (non-critical):", e)
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Reset navigation state (e.g., on logout)
   */
  function reset(): void {
    items.value = []
    isLoaded.value = false
    error.value = null
  }

  /**
   * Find a navigation item by its key
   */
  function findItemByKey(key: string): NavigationItem | undefined {
    function searchItems(itemList: NavigationItem[]): NavigationItem | undefined {
      for (const item of itemList) {
        if (item.key === key) {
          return item
        }
        if (item.children) {
          const found = searchItems(item.children)
          if (found) {
            return found
          }
        }
      }
      return undefined
    }
    return searchItems(items.value)
  }

  return {
    items,
    isLoading,
    isLoaded,
    error,
    hasItems,
    loadNavigation,
    reset,
    findItemByKey
  }
})
